// Shared money formatter (also defined in product-conversion.js; whichever loads first wins).
window.conversionFormatMoney =
  window.conversionFormatMoney ||
  ((cents, currency, locale) => {
    try {
      return new Intl.NumberFormat(locale || document.documentElement.lang || undefined, {
        style: 'currency',
        currency: currency || window.Shopify?.currency?.active,
      }).format(cents / 100);
    } catch (error) {
      return (cents / 100).toFixed(2);
    }
  });

/*
 * Cart extras: free-shipping progress bar (currency aware) and one-click upsell in the cart drawer.
 */

if (!customElements.get('free-shipping-bar')) {
  customElements.define(
    'free-shipping-bar',
    class FreeShippingBar extends HTMLElement {
      connectedCallback() {
        // Liquid already rendered the right state unless the threshold had to be converted.
        if (this.dataset.convert !== 'true') return;
        const rate = parseFloat(window.Shopify?.currency?.rate || '1');
        if (!rate || rate === 1) return;

        const threshold = Math.round(parseInt(this.dataset.threshold, 10) * rate);
        const total = parseInt(this.dataset.total, 10) || 0;
        const remaining = Math.max(threshold - total, 0);
        const percent = threshold > 0 ? Math.min(Math.floor((total / threshold) * 100), 100) : 100;

        const fill = this.querySelector('.free-shipping-bar__fill');
        const track = this.querySelector('.free-shipping-bar__track');
        const message = this.querySelector('[data-message]');
        if (fill) fill.style.setProperty('--progress', `${percent}%`);
        if (track) track.setAttribute('aria-valuenow', percent);
        if (!message) return;

        if (remaining > 0) {
          const template = this.querySelector('[data-remaining-template]');
          if (template) message.innerHTML = template.innerHTML.replace('__AMOUNT__', this.format(remaining));
        } else {
          const template = this.querySelector('[data-unlocked-template]');
          if (template) message.innerHTML = template.innerHTML;
        }
      }

      format(cents) {
        const currency = this.dataset.currency || this.dataset.cartCurrency || window.Shopify?.currency?.active;
        return window.conversionFormatMoney
          ? window.conversionFormatMoney(cents, currency, this.dataset.locale)
          : (cents / 100).toFixed(2);
      }
    }
  );
}

if (!customElements.get('cart-upsell')) {
  customElements.define(
    'cart-upsell',
    class CartUpsell extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.drawer = this.closest('cart-drawer');
        if (!this.form || !this.drawer) return; // On the cart page the plain form post reloads the cart.
        this.onSubmit = this.onSubmit.bind(this);
        this.form.addEventListener('submit', this.onSubmit);
      }

      disconnectedCallback() {
        this.form?.removeEventListener('submit', this.onSubmit);
      }

      onSubmit(event) {
        event.preventDefault();
        const button = this.form.querySelector('button[type="submit"]');
        button?.setAttribute('aria-disabled', 'true');
        button?.classList.add('loading');

        const formData = new FormData(this.form);
        formData.delete('return_to');
        formData.append(
          'sections',
          this.drawer.getSectionsToRender().map((section) => section.id)
        );
        formData.append('sections_url', window.location.pathname);

        fetch(`${window.routes.cart_add_url}`, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              // Fall back to a normal form post so the visitor still gets feedback.
              this.form.removeEventListener('submit', this.onSubmit);
              this.form.submit();
              return;
            }
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-upsell', cartData: response });
            }
            this.drawer.renderContents(response);
          })
          .catch(() => {
            this.form.removeEventListener('submit', this.onSubmit);
            this.form.submit();
          })
          .finally(() => {
            button?.removeAttribute('aria-disabled');
            button?.classList.remove('loading');
          });
      }
    }
  );
}

if (!customElements.get('free-shipping-announcement')) {
  customElements.define(
    'free-shipping-announcement',
    class FreeShippingAnnouncement extends HTMLElement {
      connectedCallback() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refresh());
      }

      disconnectedCallback() {
        this.unsubscribe?.();
      }

      refresh() {
        const sectionId = this.dataset.section;
        if (!sectionId) return;
        const root = window.Shopify?.routes?.root || '/';
        fetch(`${root}?sections=${encodeURIComponent(sectionId)}`)
          .then((response) => response.json())
          .then((sections) => {
            const html = sections[sectionId];
            if (!html) return;
            const fresh = new DOMParser().parseFromString(html, 'text/html').getElementById(this.id);
            if (fresh) {
              this.innerHTML = fresh.innerHTML;
            } else {
              this.innerHTML = '';
            }
          })
          .catch(() => {});
      }
    }
  );
}
